/**
 * Study Resource Student Level Issue Service
 * Handles API calls for student level issuing
 */

import Config from '../_config/config';

export interface StudentLevelIssue {
  Id: number;
  ResourceId: number;
  StudentId: number;
  StartDate?: string;
  EndDate?: string;
  CreationDate?: string;
  studentCode?: string;
  EnquiryId?: number;
}

export interface CreateStudentLevelIssueRequest {
  resourceId: number;
  studentCodes: string; // Comma or newline separated
  startDate?: string;
  endDate?: string;
}

class StudyResourceStudentIssueService {
  /**
   * Get authorization headers with JWT token
   */
  private getAuthHeaders(): HeadersInit {
    let token: string | null = null;
    const currentUser = localStorage.getItem('currentUser');
    if (currentUser && currentUser !== 'undefined') {
      try {
        token = JSON.parse(currentUser).token;
      } catch (e) {
        console.error('Failed to parse currentUser:', e);
      }
    }

    return {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
    };
  }

  /**
   * Get all student level issues for a resource
   */
  async getStudentLevelIssues(resourceId: number): Promise<StudentLevelIssue[]> {
    const response = await fetch(
      `${Config.nodeSiteUrl}v1/study-resource/student-issue/${resourceId}`,
      {
        method: 'GET',
        headers: this.getAuthHeaders(),
      }
    );

    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.error || `Failed to fetch student level issues: ${response.statusText}`);
    }

    const data = await response.json();
    return data.data || [];
  }

  /**
   * Create student level issue
   */
  async createStudentLevelIssue(
    issue: CreateStudentLevelIssueRequest
  ): Promise<{ success: boolean; message: string }> {
    const response = await fetch(
      `${Config.nodeSiteUrl}v1/study-resource/student-issue`,
      {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(issue),
      }
    );

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || `Failed to create student level issue: ${response.statusText}`);
    }

    return data;
  }

  /**
   * Delete student level issue
   */
  async deleteStudentLevelIssue(id: number): Promise<{ success: boolean; message: string }> {
    const response = await fetch(
      `${Config.nodeSiteUrl}v1/study-resource/student-issue/${id}`,
      {
        method: 'DELETE',
        headers: this.getAuthHeaders(),
      }
    );

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || `Failed to delete student level issue: ${response.statusText}`);
    }

    return data;
  }
}

// Export singleton instance
export const studyResourceStudentIssueService = new StudyResourceStudentIssueService();
export default studyResourceStudentIssueService;
